/**
 * Security Architecture | Perception X2 content — high-fidelity summary of security-architecture
 * Cross-reference: content/security-architecture.md
 */

import { Activity, AlertTriangle, Award, BarChart3, Brain, Briefcase, Building2, Compass, Cpu, Eye, FileText, Globe, Heart, Landmark, Layers, Lock, MessageSquare, Network, Search, Send, ShieldCheck, Sparkles, Target, Telescope, TrendingUp, Trophy, User, Users, Vote } from "lucide-react";

export const securityMeta = {
  title: "Security Architecture | Perception X2",
  description: "Explore Perception X2 security architecture: AES-256 encryption, TLS 1.3, zero-trust framework, SOC 2 Type II, ISO 27001, and sovereign infrastructure for enterprise-grade protection.",
  slug: "security",
  canonical: "https://perception.ac/security-architecture",
};

export const hero = {
  eyebrow: "Security Architecture | Perception X2",
  title: "Default Hero",
  titleAccent: "Hero Accent.",
  lede: "Perception X2 delivers autonomous capabilities powered by nine proprietary AI platforms across 50+ digital platforms.",
  trustLine: "Trusted by governments, Fortune 500 enterprises, and global institutions for 15+ years.",
  ctaPrimary: { label: "Request a Demo", href: "#contact" },
  ctaSecondary: { label: "Explore Capabilities", href: "#capabilities" },
  introBadges: ["50+ Platforms", "300-500% Penetration", "95-98% Authenticity", "15+ Years"],
  trustIndicators: ["15+ Years of Excellence", "300+ Elite Clients", "9 AI Platforms", "Zero Incidents", "99.9999% Uptime"],
  primaryAccent: "var(--color-crimson)",
  secondaryAccent: "var(--color-cyan)",
};

export const executiveSummary = {
  eyebrow: "Executive Summary",
  title: "Capability Overview",
  intro: "Perception X2 transforms Security Architecture | Perception X2 from periodic reporting into a continuously operating strategic capability.",
  paragraphs: [
    "Perception X2 monitors 50+ digital platforms and 100,000+ news sources continuously, capturing every signal that affects organizational position.",
    "With 144-dimensional emotional mapping, our systems provide analytical depth no conventional tool can match.",
    "For over 15 years, CryptoMize has served governments, Fortune 500 enterprises, and global institutions with capabilities that operate at a scale and precision impossible for traditional agencies.",
  ],
  metrics: [
    { value: "50+", label: "Platforms", icon: Globe, accent: "var(--color-cyan)" },
    { value: "144-D", label: "Emotional Mapping", icon: Heart, accent: "var(--color-crimson)" },
    { value: "95-98%", label: "Authenticity", icon: Award, accent: "var(--color-amber)" },
    { value: "15+", label: "Years Operational", icon: Trophy, accent: "var(--color-emerald)" },
    { value: "0", label: "Security Incidents", icon: ShieldCheck, accent: "var(--color-purple)" },
  ],
  cta: { label: "Explore the Capability", href: "#capabilities" },
};

export const problem = {
  eyebrow: "The Problem",
  title: "Why Traditional Approaches Fall Short",
  lede: "Most organizations operate with capabilities that are fragmented, reactive, and limited in scope.",
  accent: "var(--color-amber)",
  pillars: [
    { title: "Operational Gap", body: "Traditional approaches operate at human speed while the digital ecosystem moves at machine speed. The gap is where outcomes are lost.", icon: Activity, accent: "var(--color-crimson)" },
    { title: "Coverage Limitation", body: "No single team can monitor 50+ platforms and 100,000+ sources manually. Organizations miss critical signals.", icon: Network, accent: "var(--color-amber)" },
    { title: "Surface-Level Analysis", body: "Most tools report mentions and sentiment. They cannot reveal the patterns and dynamics that determine outcomes.", icon: Search, accent: "var(--color-cyan)" },
    { title: "Coordination Overhead", body: "Manual coordination across teams, platforms, and stakeholders produces delays that lose critical windows.", icon: Users, accent: "var(--color-purple)" },
  ],
};

export const solution = {
  eyebrow: "The Solution",
  title: "Autonomous Capability at Machine Speed",
  intro: "Perception X2 transforms operations from reactive manual effort to proactive autonomous deployment.",
  accent: "var(--color-crimson)",
  pillarAccents: ["var(--color-crimson)", "var(--color-cyan)", "var(--color-amber)", "var(--color-emerald)"],
  pillars: [
    { title: "Continuous Multi-Platform Monitoring", body: "Our AI monitors 50+ platforms and 100,000+ news sources in a unified system. No silos, no gaps.", icon: Globe },
    { title: "144-Dimensional Emotional Analysis", body: "Beyond surface-level sentiment, we map signals across 144 distinct emotional dimensions.", icon: Heart },
    { title: "Sub-Second Real-Time Processing", body: "Critical signals surfaced as they emerge, not after they have cascaded.", icon: Activity },
    { title: "Predictive Trajectory Modeling", body: "Beyond reporting what is happening, our models forecast what will happen.", icon: TrendingUp },
  ],
  cta: { label: "Explore the Capability", href: "#capabilities" },
};

export const capabilityOverview = {
  eyebrow: "Service Overview",
  title: "Comprehensive Capability Stack",
  intro: "Perception X2 provides end-to-end capability deployment — from multi-platform monitoring through 144-dimensional emotional analysis to predictive trajectory modeling.",
  primaryAccent: "var(--color-crimson)",
  secondaryAccent: "var(--color-cyan)",
  tertiaryAccent: "var(--color-amber)",
  frameworks: [
    { name: "Multi-Platform Monitoring", description: "Continuous monitoring across 50+ digital platforms and 100,000+ news sources." },
    { name: "144-Dimensional Emotional Mapping", description: "Map signals across 144 distinct emotional dimensions." },
    { name: "Predictive Trajectory Modeling", description: "Forecast signal evolution 7-90 days ahead. See what comes next." },
    { name: "Cross-Platform Correlation", description: "Patterns detected across platforms that surface-level monitoring misses." },
    { name: "Real-Time Performance Analytics", description: "Real-time measurement of effectiveness, penetration, and resonance." },
  ],
  biasLevels: [
    { title: "Unified Visibility", description: "50+ platforms in a single intelligence layer" },
    { title: "144-D Depth", description: "Emotional mapping across 144 distinct dimensions" },
    { title: "Sub-Second Processing", description: "Real-time intelligence with sub-second latency" },
    { title: "Predictive Forecasting", description: "Trajectory modeling for emerging trends" },
  ],
  variantDimensions: [
    { label: "Coverage", description: "50+ platforms and 100K+ news sources" },
    { label: "Depth", description: "144-dimensional emotional mapping" },
    { label: "Speed", description: "Sub-second processing latency" },
    { label: "Forecast Horizon", description: "7-90 day predictive trajectory modeling" },
  ],
  localization: [
    "Calibrated for 85+ regions and 15+ languages",
    "Region-specific signal patterns and cultural context",
    "Local platform dynamics and engagement patterns",
    "Cross-cultural signal resonance optimization",
  ],
};

export const howItWorks = {
  eyebrow: "How It Works",
  title: "From Signal Detection to Strategic Intelligence",
  accent: "var(--color-cyan)",
  stepAccents: ["var(--color-crimson)", "var(--color-cyan)", "var(--color-amber)", "var(--color-emerald)", "var(--color-purple)"],
  steps: [
    { number: "01", title: "Continuous Multi-Platform Collection", body: "Our AI continuously collects data from 50+ platforms and 100,000+ news sources." },
    { number: "02", title: "144-Dimensional Emotional Analysis", body: "Every signal is mapped across 144 emotional dimensions." },
    { number: "03", title: "Cross-Platform Pattern Detection", body: "Machine learning models identify patterns across platforms." },
    { number: "04", title: "Predictive Trajectory Modeling", body: "Historical patterns feed predictive models that forecast emerging trends." },
    { number: "05", title: "Strategic Intelligence Reporting", body: "Intelligence delivered through executive dashboards, automated briefings, and configurable alerts." },
  ],
};

export const coreFeatures = {
  eyebrow: "Core Features",
  title: "The Engine Behind the Capability",
  accent: "var(--color-crimson)",
  featureAccents: ["var(--color-crimson)", "var(--color-cyan)", "var(--color-amber)", "var(--color-emerald)", "var(--color-purple)", "var(--color-crimson)", "var(--color-violet)", "var(--color-rose)"],
  features: [
    { number: "01", title: "Multi-Platform Real-Time Monitoring", body: "Continuous surveillance across 50+ digital platforms with sub-second processing." },
    { number: "02", title: "144-Dimensional Emotional Mapping", body: "Map signals across 144 distinct emotional dimensions." },
    { number: "03", title: "Predictive Trajectory Forecasting", body: "Forecast signal evolution 7-90 days ahead." },
    { number: "04", title: "Cross-Platform Correlation Engine", body: "Patterns detected across platforms that single-platform monitoring misses." },
    { number: "05", title: "Stakeholder Intelligence Mapping", body: "Comprehensive mapping of stakeholder positions and influence patterns." },
    { number: "06", title: "Automated Executive Briefings", body: "Configurable intelligence briefings delivered on your cadence." },
    { number: "07", title: "Real-Time Alert System", body: "Critical signals surface as alerts the moment they emerge." },
    { number: "08", title: "Multi-Language Coverage", body: "15+ languages with cultural context awareness." },
  ],
};

export const advancedFeatures = {
  eyebrow: "Advanced Features",
  title: "Enterprise-Grade Capabilities",
  sectionAccent: "var(--color-cyan)",
  features: [
    { title: "Predictive Pattern Recognition", body: "Detect statistically significant deviations from baseline patterns. Anomalies often signal emerging opportunities or threats before they become visible.", icon: Sparkles, accent: "var(--color-cyan)" },
    { title: "Influence Network Mapping", body: "Map stakeholder influence networks across platforms. Identify key nodes, propagation pathways, and amplification opportunities.", icon: Network, accent: "var(--color-amber)" },
    { title: "Strategic Intelligence Briefings", body: "Automated intelligence briefings with customized depth and focus areas.", icon: Briefcase, accent: "var(--color-emerald)" },
    { title: "Cross-Platform Coordination", body: "Coordinated deployment across 50+ platforms with platform-native optimization.", icon: Globe, accent: "var(--color-purple)" },
  ],
};

export const useCases = {
  eyebrow: "Industry Applications",
  title: "Capability Applications Across Sectors",
  accent: "var(--color-crimson)",
  cases: [
    { title: "Enterprise Communications", body: "Comprehensive enterprise communications across markets and stakeholder groups.", metric: "Enterprise Scale", icon: Building2, accent: "var(--color-crimson)" },
    { title: "Government and Public Sector", body: "Government communications for public trust, policy positioning, and civic engagement.", metric: "Public Trust", icon: Landmark, accent: "var(--color-cyan)" },
    { title: "Crisis Response", body: "Rapid-response capability for crisis events with sub-hour activation.", metric: "Sub-Hour Response", icon: AlertTriangle, accent: "var(--color-amber)" },
    { title: "Market Expansion", body: "Enter new markets through coordinated strategic presence.", metric: "Market Entry", icon: Globe, accent: "var(--color-emerald)" },
    { title: "Public Figure Communications", body: "Strategic communications for public figures and HNWIs.", metric: "Reputation", icon: User, accent: "var(--color-purple)" },
    { title: "Political Communications", body: "Political communications for campaigns, policy, and voter engagement.", metric: "Campaign Reach", icon: Vote, accent: "var(--color-denim)" },
  ],
};

export const benefits = {
  eyebrow: "Benefits",
  title: "Why This Capability Matters",
  accent: "var(--color-amber)",
  gridCols: 2 as const,
  benefits: [
    { title: "Comprehensive Coverage", body: "50+ platforms and 100K+ sources in a single intelligence layer. No gaps, no silos.", icon: Globe, accent: "var(--color-crimson)" },
    { title: "Real-Time Awareness", body: "Sub-second processing ensures critical signals are surfaced as they emerge.", icon: Activity, accent: "var(--color-cyan)" },
    { title: "Deep Analytical Insight", body: "144-dimensional emotional mapping reveals resonance patterns no surface-level analysis can match.", icon: Heart, accent: "var(--color-amber)" },
    { title: "Predictive Foresight", body: "Trajectory modeling forecasts emerging trends and shifts 7-90 days ahead.", icon: TrendingUp, accent: "var(--color-emerald)" },
    { title: "Cross-Platform Patterns", body: "Correlation across platforms reveals patterns single-platform monitoring misses.", icon: Network, accent: "var(--color-purple)" },
    { title: "Strategic Decision Support", body: "Executive briefings deliver intelligence when and how decision-makers need it.", icon: Briefcase, accent: "var(--color-denim)" },
  ],
};

export const usps = {
  eyebrow: "Our USPs",
  title: "The Perception X2 Difference",
  intro: "No other platform combines 50+ platform coverage, 144-dimensional emotional mapping, and predictive trajectory modeling in a single autonomous system.",
  accent: "var(--color-crimson)",
  gridCols: 2 as const,
  usps: [
    { title: "50+ Platform Coverage", body: "Continuous monitoring across 50+ digital platforms and 100,000+ news sources.", icon: Globe, accent: "var(--color-crimson)" },
    { title: "144-Dimensional Emotional Mapping", body: "Analytical depth no conventional tool provides.", icon: Heart, accent: "var(--color-cyan)" },
    { title: "Sub-Second Processing", body: "Real-time intelligence with sub-second latency.", icon: Activity, accent: "var(--color-amber)" },
    { title: "15+ Years of Intelligence", body: "Every engagement contributes to longitudinal intelligence datasets.", icon: Trophy, accent: "var(--color-emerald)" },
  ],
  cta: { label: "Explore Other Services", href: "/platform/capabilities" },
};

export const whoItsFor = {
  eyebrow: "Who It's For",
  title: "Organizations That Require This Capability",
  accent: "var(--color-cyan)",
  gridCols: 2 as const,
  segments: [
    { title: "Fortune 500 and Global Enterprises", body: "Multi-national organizations requiring comprehensive capability deployment.", icon: Building2, accent: "var(--color-crimson)" },
    { title: "Government and Sovereign Entities", body: "Government organizations requiring strategic capability for public trust.", icon: Landmark, accent: "var(--color-cyan)" },
    { title: "Technology Companies", body: "Innovation-driven organizations where capability drives market positioning.", icon: Cpu, accent: "var(--color-amber)" },
    { title: "Financial Institutions", body: "Financial organizations where capability drives investor confidence.", icon: BarChart3, accent: "var(--color-emerald)" },
    { title: "Healthcare Organizations", body: "Healthcare organizations where capability drives patient trust.", icon: Heart, accent: "var(--color-purple)" },
    { title: "Public Figures and HNWIs", body: "High-profile individuals where capability drives personal brand protection.", icon: User, accent: "var(--color-denim)" },
  ],
};

export const whyChooseUs = {
  eyebrow: "Why Choose Us",
  title: "The Industry Standard",
  accent: "var(--color-amber)",
  gridCols: 2 as const,
  reasons: [
    { title: "Industry-Leading Coverage", body: "50+ platforms and 100K+ sources in a unified intelligence layer.", icon: Globe, accent: "var(--color-crimson)" },
    { title: "144-Dimensional Depth", body: "Signals mapped across 144 distinct emotional dimensions.", icon: Heart, accent: "var(--color-cyan)" },
    { title: "15+ Years of Intelligence", body: "Every engagement contributes to a longitudinal intelligence dataset.", icon: Trophy, accent: "var(--color-amber)" },
    { title: "Zero Security Incidents in 15+ Years", body: "Your infrastructure is protected by standards that exceed enterprise requirements.", icon: ShieldCheck, accent: "var(--color-emerald)" },
  ],
};

export const relatedCapabilities = {
  eyebrow: "Related Services",
  title: "Expand Your Capability",
  accent: "var(--color-crimson)",
  capabilities: [
    { name: "Digital Intelligence", href: "/services/digital-intelligence-monitoring", body: "Comprehensive digital intelligence powered by Perception X2.", icon: Eye, accent: "var(--color-cyan)" },
    { name: "Brand Protection", href: "/services/brand-protection-augmentation", body: "Active brand defense powered by Perception X2.", icon: ShieldCheck, accent: "var(--color-amber)" },
  ],
  engines: [
    { name: "Cognitive Resonance Engine", href: "/engines/cognitive-resonance", body: "Engine that powers 144-dimensional emotional mapping.", icon: Cpu, accent: "var(--color-purple)" },
  ],
  services: [
    { name: "Crisis Management", href: "/services/crisis-management-response", body: "Rapid-response crisis management powered by Perception X2.", icon: AlertTriangle, accent: "var(--color-crimson)" },
  ],
};

export const faq = {
  eyebrow: "Frequently Asked Questions",
  title: "Security Architecture | Perception X2 — Common Questions",
  accent: "var(--color-purple)",
  items: [
    { q: "What is this capability?", a: "Perception X2's capability combines multi-platform monitoring, 144-dimensional emotional mapping, predictive trajectory modeling, and autonomous deployment in a single integrated system." },
    { q: "How does AI improve this capability?", a: "AI processes millions of data points across 50+ platforms continuously, identifies patterns human analysts miss, and delivers predictive modeling with sub-second latency." },
    { q: "How many platforms does Perception X2 cover?", a: "Perception X2 monitors 50+ digital platforms and 100,000+ news sources continuously." },
    { q: "What is 144-dimensional emotional mapping?", a: "144-dimensional emotional mapping analyzes signals across 144 distinct emotional dimensions — far exceeding the 5-10 dimensions used by conventional tools." },
    { q: "How quickly can this be deployed?", a: "Standard configurations achieve sub-hour activation. Complex enterprise deployments complete within 4-6 weeks." },
    { q: "What is predictive trajectory modeling?", a: "Predictive trajectory modeling forecasts signal evolution 7-90 days ahead with industry-leading accuracy." },
    { q: "What languages does Perception X2 support?", a: "Perception X2 operates in 15+ languages with cultural context awareness." },
    { q: "Is this compliant with regulations?", a: "Yes. Perception X2 operates in full compliance with GDPR, CCPA, SOC 2 Type II, and ISO 27001 standards." },
    { q: "How is this different from conventional tools?", a: "Conventional tools report mentions and sentiment. Perception X2 provides unified visibility, 144-dimensional depth, predictive modeling, and autonomous deployment." },
    { q: "What is sub-second processing?", a: "Sub-second processing means signals are processed and surfaced in less than one second from detection." },
  ],
};

export const paa = {
  eyebrow: "People Also Ask",
  title: "Security Architecture | Perception X2 — What People Ask",
  accent: "var(--color-cyan)",
  items: [
    { q: "What is this capability and why does it matter?", a: "This capability determines whether organizations lead or follow in dynamic perception environments. It provides comprehensive visibility, 144-dimensional depth, and predictive forecasting." },
    { q: "How does AI-powered capability work?", a: "AI processes millions of data points across 50+ digital platforms, identifies patterns, and delivers predictive modeling." },
    { q: "What is 144-dimensional analysis?", a: "144-dimensional analysis evaluates signals across 144 distinct emotional dimensions." },
    { q: "What is predictive trajectory modeling?", a: "Predictive trajectory modeling forecasts signal evolution 7-90 days ahead with industry-leading accuracy." },
    { q: "How does this differ from conventional tools?", a: "Conventional tools provide surface-level visibility. This capability provides unified multi-platform depth, predictive forecasting, and autonomous deployment." },
  ],
};

export const cta = {
  eyebrow: "Get Started",
  title: "Ready to Engage Perception X2.",
  intro: "Every day without this capability is a day your organization operates with limited visibility. Perception X2 delivers 50+ platform coverage, 144-dimensional emotional mapping, and predictive trajectory modeling that transforms capability into strategic advantage.",
  promises: [
    "Our team will demonstrate how Perception X2 transforms capability deployment",
    "All consultations are conducted under strict confidentiality with zero security incidents in 15+ years of operations",
  ],
  ctaPrimary: { label: "Contact Us", href: "/#contact" },
  ctaSecondary: { label: "Schedule a Demo", href: "/#contact" },
  trustBar: ["50+ Platforms", "144-D Mapping", "15+ Years", "Zero Incidents", "99.9999% Uptime"],
  accent: "var(--color-crimson)",
  secondaryAccent: "var(--color-cyan)",
};
